import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [transitionState, setTransitionState] = useState('exited'); // entering, entered, exiting, exited
  const [customJson, setCustomJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [tokenStats, setTokenStats] = useState({
    promptTokens: 0,
    completionTokens: 0,
    totalTokens: 0,
    estimatedCost: 0
  });

  const messagesEndRef = useRef(null);

  // Helper to load catalog and build Gemini System Prompt
  const loadAndBuildCatalog = async (customCatalogRawText) => {
    try {
      let data = null;
      if (customCatalogRawText && customCatalogRawText.trim()) {
        try {
          let sanitizedText = customCatalogRawText.trim();
          // Strip comments robustly without breaking URLs (https://)
          sanitizedText = sanitizedText.replace(
            /("(?:\\.|[^"\\])*")|('(?:\\.|[^'\\])*')|(\/\*[\s\S]*?\*\/)|(\/\/.*$)/gm,
            (match, g1, g2, g3, g4) => {
              if (g3 || g4) { return ''; } // Strip comments
              return match; // Keep string literals
            }
          );
          // Remove trailing commas
          sanitizedText = sanitizedText.replace(/,(\s*[\]}])/g, '$1');
          
          data = JSON.parse(sanitizedText);
        } catch (e) {
          console.error('Failed to parse custom catalog/context JSON', e);
        }
      }

      // Fallback to public/product.json if no data parsed
      if (!data) {
        const response = await fetch('/product.json');
        data = await response.json();
      }

      // Determine the type of the parsed data
      if (Array.isArray(data)) {
        // Format A: Standard Product Catalog List
        setProducts(data);

        const compactCatalog = data.map((p) => {
          return `ID: ${p.id || 'N/A'} | Ürün Adı: ${p.title || 'Belirtilmemiş'} | Kategori: ${p.category || 'Belirtilmemiş'} | Fiyat: $${p.price || '0.00'} | Stil: ${p.sub || 'Belirtilmemiş'} | Açıklama: ${(p.description || '').trim()} | Kumaş Detayları: ${p.fabric_details || 'Belirtilmemiş'}`;
        }).join('\n');

        const prompt = `You are a helpful, professional, and friendly AI Shopping Assistant for the "Quin" E-commerce store.
You have access to the complete product catalog of Quin:
${compactCatalog}

Guidelines:
1. Always respond in the language the user speaks to you (default is Turkish, but reply in English if they speak English).
2. Answer questions accurately using only the product details, prices, and fabric compositions provided above.
3. Keep answers clean, friendly, readable, and structured. Use bullet points or emojis when helpful.
4. When suggesting products, mention their names exactly as in the catalog (e.g. "19223 - Regular Fit Jeans") and state their exact price (e.g., "$29.95").
5. Do not make up products. If we do not have a certain item, politely say so and recommend the closest alternative we have.
6. If the user asks about general, non-shopping questions (like "what is the capital of France"), answer briefly but quickly guide them back to our products.`;

        setSystemPrompt(prompt);
      } else if (data && typeof data === 'object') {
        // Format B: Session State Context JSON (User's special structure)
        const cp = data.current_product || {};
        const recentlyViewed = data.recently_viewed_products || [];
        const propensity = data.propensity || {};
        const segment = data.segment || {};
        const behavioralState = data.behavioral_state || {};
        const journey = data.journey || {};
        const primaryInterest = data.primary_interest || {};
        const experience = data.quin_experience_context || {};
        const guardrails = data.guardrails || {};

        // Ingest current product and recently viewed products in products array for suggestions
        const allProducts = [];
        if (cp.id) {
          allProducts.push({
            id: cp.id,
            title: cp.name || cp.title || 'Seçili Ürün',
            price: cp.price || 0,
            category: cp.category || '',
            sub: cp.seller_id || '',
            description: cp.category_path || '',
            fabric_details: '',
            images: cp.images || { main: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=500' }
          });
        }

        recentlyViewed.forEach((p) => {
          if (p.id && String(p.id) !== String(cp.id)) {
            allProducts.push({
              id: p.id,
              title: p.name || p.title || 'Geçmiş Ürün',
              price: p.price || 0,
              category: p.category || '',
              sub: p.seller_id || '',
              description: p.category_path || '',
              fabric_details: '',
              images: p.images || { main: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=500' }
            });
          }
        });

        setProducts(allProducts);

        // Format recently viewed products list for the LLM
        const recentlyViewedText = recentlyViewed.map((p) => {
          return `- ID: ${p.id} | Ürün Adı: ${p.name || p.title} | Fiyat: ${p.price} ${p.currency || 'TRY'} | Kategori: ${p.category || 'N/A'}`;
        }).join('\n');

        // Campaigns text extraction
        const campaignsText = (experience.campaigns || []).map((c) => {
          return `- Kampanya: ${c.campaign_name || 'N/A'} | Tür: ${c.campaign_type || 'N/A'} | Açıklama: ${c.campaign_description || 'N/A'}${c.discount_code ? ` | İNDİRİM KODU: ${c.discount_code}` : ''}`;
        }).join('\n');

        // Guardrails text extraction
        const guardrailsText = Object.entries(guardrails).map(([k, v]) => {
          return `- ${k}: ${v ? 'EVET (Zorunlu Kural)' : 'HAYIR'}`;
        }).join('\n');

        const prompt = `You are a helpful, professional, and friendly AI Shopping Assistant for the "Quin" E-commerce store.
You have been injected with the LIVE active session context of the user:

[USER PROFILE & BEHAVIOR]
- User Segment: ${segment.label || 'N/A'} (${segment.description || 'N/A'})
- Behavioral State: ${behavioralState.label || 'N/A'} (${behavioralState.description || 'N/A'})
- Journey Stage: ${journey.current_stage || 'N/A'} (${journey.current_stage_meaning || 'N/A'})
- Dominant Propensity: ${propensity.dominant_propensity || 'N/A'}
- Buyer Strength: ${propensity.buyer_strength || 'N/A'}
- Abandoner Strength: ${propensity.abandoner_strength || 'N/A'}
- Purchase Readiness: ${journey.purchase_readiness || 'N/A'}

[CURRENTLY VIEWED PRODUCT]
- Product ID: ${cp.id || 'N/A'}
- Product Name: ${cp.name || 'N/A'}
- Category: ${cp.category || 'N/A'} (Path: ${cp.category_path || 'N/A'})
- Seller: ${cp.seller_id || 'N/A'}
- Price: ${cp.price || '0'} ${cp.currency || 'TRY'}
- Has Active Discount: ${cp.has_discount ? 'Yes' : 'No'}

[RECENTLY VIEWED PRODUCTS HISTORY (CUMULATIVE)]
${recentlyViewedText ? recentlyViewedText : 'No other products viewed in history yet.'}

[PRIMARY INTEREST]
- Category: ${primaryInterest.category || 'N/A'}
- Avg Interest Price: ${primaryInterest.average_price || 'N/A'}

[ACTIVE CAMPAIGNS & EXPERIENCE]
- Has Active Campaign: ${experience.has_active_campaign ? 'Yes' : 'No'}
- Description: ${experience.experience_description || 'N/A'}
${campaignsText ? campaignsText : 'No active campaigns.'}

[STRICT GUARDRAILS]
${guardrailsText ? guardrailsText : 'No custom guardrails.'}
- Never invent discount codes. Only offer discount codes if they are explicitly listed in the active campaigns section above (e.g., if active discount codes are provided, you may share them contextually when they are evaluating or hesitating).
- Do not claim exclusivity or urgency unless explicitly instructed.
- Do not make product claims without provided data.

Guidelines:
1. Always respond in the language the user speaks to you (default is Turkish, but reply in English if they speak English).
2. Tailor your tone and recommendation aggressiveness based on the User Profile (e.g. if purchase_readiness is low/early, be helpful and informative; if they are close to checkout or have discount campaigns, assist them gently to convert).
3. You have access to both the currently viewed product and the history of recently viewed products. Suggest them contextually where relevant.
4. If there is an active discount campaign with a coupon code, present it contextually if the user is looking for a discount or hesitating to purchase.
5. Keep answers friendly, premium, readable, and structured. Use bullet points or emojis when helpful.`;

        setSystemPrompt(prompt);
      }
    } catch (err) {
      console.error('Failed to load product catalog for chatbot:', err);
    }
  };

  // 1. Load settings on mount
  useEffect(() => {
    const envKey = process.env.REACT_APP_GEMINI_API_KEY || '';
    const savedCatalogText = localStorage.getItem('gemini_custom_catalog') || '';
    const savedTokenStats = localStorage.getItem('gemini_token_stats');
    
    if (envKey) {
      setApiKey(envKey);
    }

    if (savedTokenStats) {
      try {
        setTokenStats(JSON.parse(savedTokenStats));
      } catch (e) {
        console.error('Failed to parse token stats', e);
      }
    }

    setCustomJson(savedCatalogText);
    loadAndBuildCatalog(savedCatalogText);

    // Load initial welcome message
    setMessages([
      {
        sender: 'ai',
        text: 'Merhaba! Ben Quin Akıllı Alışveriş Asistanıyım. Size ürünlerimiz hakkında bilgi verebilir, beden/kategori önerilerinde bulunabilir veya alışverişinizde yardımcı olabilirim. Nasıl yardımcı olabilirim? 🛍️',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  }, []);

  // Sync isOpen with transitionState for smooth slide-in/out animations
  useEffect(() => {
    if (isOpen) {
      setTransitionState('entering');
      const timer = setTimeout(() => {
        setTransitionState('entered');
      }, 50);
      return () => {
        clearTimeout(timer);
      };
    } else {
      setTransitionState('exiting');
      const timer = setTimeout(() => {
        setTransitionState('exited');
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  // 3. Simulated Delta Status updates are injected via handleSaveSettings (Settings Paste JSON)

  const handleSaveSettings = (e) => {
    e.preventDefault();
    setJsonError('');

    let isSession = false;
    let isCatalogUpdated = false;
    let stringified = '';

    // Validate JSON if not empty
    if (customJson.trim()) {
      try {
        let sanitizedText = customJson.trim();
        // Strip comments robustly without breaking URLs (https://)
        sanitizedText = sanitizedText.replace(
          /("(?:\\.|[^"\\])*")|('(?:\\.|[^'\\])*')|(\/\*[\s\S]*?\*\/)|(\/\/.*$)/gm,
          (match, g1, g2, g3, g4) => {
            if (g3 || g4) { return ''; } // Strip comments
            return match; // Keep string literals
          }
        );
        // Remove trailing commas
        sanitizedText = sanitizedText.replace(/,(\s*[\]}])/g, '$1');

        const parsedNew = JSON.parse(sanitizedText);
        
        const isNewArray = Array.isArray(parsedNew);
        const isNewObject = parsedNew && typeof parsedNew === 'object' && !Array.isArray(parsedNew);
        
        if (!isNewArray && !isNewObject) {
          setJsonError('Hata: JSON geçersiz. Bir ürün dizisi (array) veya geçerli bir oturum bağlam nesnesi (object) olmalıdır.');
          return;
        }
        
        isSession = isNewObject;
        isCatalogUpdated = true;

        const finalData = parsedNew;
        stringified = JSON.stringify(finalData, null, 2);
        localStorage.setItem('gemini_custom_catalog', stringified);
        setCustomJson(stringified); // Pre-fill textarea with the validated JSON!
        loadAndBuildCatalog(stringified);
      } catch (err) {
        setJsonError(`Geçersiz JSON formatı: ${err.message}`);
        return;
      }
    } else {
      const wereSettingsCustom = !!localStorage.getItem('gemini_custom_catalog');
      if (wereSettingsCustom) {
        localStorage.removeItem('gemini_custom_catalog');
        loadAndBuildCatalog('');
        isCatalogUpdated = true;
      }
    }

    setShowSettings(false);

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isCatalogUpdated) {
      if (stringified) {
        // Inject the newly pasted JSON as a simulated hidden status update!
        const hiddenMsg = {
          sender: 'user',
          text: `[SİSTEM OTURUM GÜNCELLEMESİ - YENİ DURUM ENJEKSİYONU]:\n${stringified}`,
          isHiddenSystem: true,
          timestamp
        };

        const successMsgText = isSession
          ? '⚙️ [Sistem Bildirimi]: Oturum bağlamı başarıyla enjekte edildi! Asistan artık bu oturum verilerine göre yanıt verecektir.'
          : '⚙️ [Sistem Bildirimi]: Ürün kataloğu başarıyla enjekte edildi!';

        const visibleMsg = {
          sender: 'ai',
          text: successMsgText,
          isSystem: true,
          timestamp
        };

        setMessages((prev) => [...prev, hiddenMsg, visibleMsg]);
      } else {
        // Catalog was cleared
        const visibleMsg = {
          sender: 'ai',
          text: '⚙️ [Sistem Bildirimi]: Özel ürün kataloğu/bağlamı temizlendi. Varsayılan katalog aktif.',
          isSystem: true,
          timestamp
        };
        setMessages((prev) => [...prev, visibleMsg]);
      }
    }
  };

  const handleClearCatalog = () => {
    localStorage.removeItem('gemini_custom_catalog');
    setCustomJson('');
    setJsonError('');
    loadAndBuildCatalog('');
    setShowSettings(false);

    setMessages(prev => [
      ...prev,
      {
        sender: 'ai',
        text: 'Ürün kataloğu varsayılan (product.json) ayarlarına sıfırlandı! 📋🔄',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  };

  const handleResetTokenStats = () => {
    const cleared = {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      estimatedCost: 0
    };
    setTokenStats(cleared);
    localStorage.setItem('gemini_token_stats', JSON.stringify(cleared));
  };

  // Extract products mentioned in a message
  const getProductSuggestions = (messageText) => {
    if (!products.length || !messageText) {
      return [];
    }
    
    // Scan for matches in product titles
    return products.filter(product => {
      const cleanTitle = product.title.toLowerCase();
      const cleanMsg = messageText.toLowerCase();
      
      // Look for full title or product ID or distinct words of title
      return cleanMsg.includes(cleanTitle) || 
             cleanMsg.includes(product.id.toString()) ||
             (product.title.split(' - ')[1] && cleanMsg.includes(product.title.split(' - ')[1].toLowerCase()));
    }).slice(0, 3); // Max 3 suggestions to avoid crowding
  };

  // 5. Send message to Gemini API
  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputMessage;
    if (!text.trim()) {
      return;
    }

    // Add user message
    const userMsg = {
      sender: 'user',
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    if (!apiKey) {
      setIsLoading(false);
      setMessages(prev => [
        ...prev,
        {
          sender: 'ai',
          text: 'Gemini API anahtarı eksik. Sohbet robotunu etkinleştirmek için lütfen sağ üstteki ayarlar (⚙️) butonuna basarak Google Gemini API anahtarınızı girin.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isSystem: true
        }
      ]);
      return;
    }

    try {
      // Map existing messages to raw history
      const rawHistory = messages
        .filter(m => !m.isSystem)
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'model',
          text: m.text
        }));

      // Append current message
      rawHistory.push({
        role: 'user',
        text: text
      });

      // Combine consecutive turns with the same role to comply with Gemini alternating turn constraint
      const history = [];
      rawHistory.forEach((item) => {
        if (history.length > 0 && history[history.length - 1].role === item.role) {
          history[history.length - 1].parts[0].text += `\n\n${item.text}`;
        } else {
          history.push({
            role: item.role,
            parts: [{ text: item.text }]
          });
        }
      });

      const requestBody = {
        contents: history,
        systemInstruction: {
          parts: {
            text: systemPrompt
          }
        }
      };

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        let errMessage = `API hatası (Durum: ${response.status})`;
        try {
          const errData = await response.json();
          if (errData.error && errData.error.message) {
            errMessage = `API Hatası: ${errData.error.message}`;
          }
        } catch (err) {
          console.warn('Could not parse error JSON response', err);
        }
        throw new Error(errMessage);
      }

      const resData = await response.json();

      // Extract token usage metadata if available
      if (resData.usageMetadata) {
        const prompt = resData.usageMetadata.promptTokenCount || 0;
        const completion = resData.usageMetadata.candidatesTokenCount || 0;
        const total = resData.usageMetadata.totalTokenCount || (prompt + completion);
        
        // Gemini 1.5/3.5 Flash pricing:
        // Input: $0.075 per 1M tokens ($0.000000075 / token)
        // Output: $0.30 per 1M tokens ($0.0000003 / token)
        const addedCost = (prompt * 0.000000075) + (completion * 0.0000003);

        setTokenStats(prev => {
          const updated = {
            promptTokens: prev.promptTokens + prompt,
            completionTokens: prev.completionTokens + completion,
            totalTokens: prev.totalTokens + total,
            estimatedCost: prev.estimatedCost + addedCost
          };
          localStorage.setItem('gemini_token_stats', JSON.stringify(updated));
          return updated;
        });
      }
      
      let aiText = '';
      if (resData.candidates && resData.candidates[0] && resData.candidates[0].content) {
        aiText = resData.candidates[0].content.parts[0].text;
      } else {
        aiText = 'Üzgünüm, şu an yanıt oluşturamadım. Lütfen tekrar dener misiniz?';
      }

      setMessages(prev => [
        ...prev,
        {
          sender: 'ai',
          text: aiText,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } catch (error) {
      console.error('Error communicating with Gemini API:', error);
      const displayedError = error.message.startsWith('API Hatası:')
        ? `Bağlantı başarısız oldu. ${error.message}`
        : 'Bağlantı sırasında bir hata oluştu. Lütfen API anahtarınızı veya internet bağlantınızı kontrol edip tekrar deneyin.';
      setMessages(prev => [
        ...prev,
        {
          sender: 'ai',
          text: displayedError,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const suggestionChips = [
    'En popüler ürünler hangileri? 🔥',
    'Yeni sezon ceketleri göster 🧥',
    'Kot pantolon fiyatları ne kadar? 👖',
    'Mağazada hangi aksesuarlar var? ⌚'
  ];

  return (
    <>
      {/* Floating Trigger Button */}
      <button 
        className={`chatbot-trigger ${isOpen ? 'open' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Quin AI Assistant"
      >
        {isOpen ? (
          <i className="fa fa-times"></i>
        ) : (
          <i className="fa fa-comments"></i>
        )}
      </button>

      {/* Chat Widget Window */}
      {transitionState !== 'exited' && (
        <div className={`chatbot-window ${transitionState}`}>
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <h3 className="chat-header-title">
                <i className="fa fa-android"></i> Quin Asistan
              </h3>
              <p className="chat-header-status">
                <span className="status-dot"></span> Gemini 3.5 Flash Çevrimiçi
              </p>
            </div>
            <div className="chat-header-actions">
              <button 
                className="chat-btn-icon" 
                onClick={() => setShowSettings(!showSettings)}
                title="Gemini API Ayarları"
              >
                <i className="fa fa-cog"></i>
              </button>
              <button 
                className="chat-btn-icon" 
                onClick={() => setIsOpen(false)}
                title="Kapat"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>

          {/* Settings Overlay */}
          {showSettings && (
            <div className="settings-overlay">
              <div className="settings-overlay-header">
                <h4 className="settings-overlay-title">Gemini & Bağlam Ayarları</h4>
                <button 
                  className="chat-btn-icon" 
                  style={{ color: '#4b5563', background: 'rgba(0,0,0,0.05)' }}
                  onClick={() => setShowSettings(false)}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              {/* Token & Cost Monitor Dashboard */}
              <div className="token-monitor-card mb-3 p-3 rounded" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: '12px'
              }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="m-0" style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
                    <i className="fa fa-bar-chart me-1 text-primary"></i> Token ve Maliyet Raporu
                  </h5>
                  <button 
                    type="button" 
                    onClick={handleResetTokenStats}
                    className="btn btn-link p-0 text-decoration-none text-muted"
                    style={{ fontSize: '11px' }}
                    title="İstatistikleri Sıfırla"
                  >
                    <i className="fa fa-refresh me-1"></i> Sıfırla
                  </button>
                </div>
                <div className="row g-2 text-center" style={{ fontSize: '11px' }}>
                  <div className="col-4">
                    <div className="p-2 rounded bg-white border border-light">
                      <div className="text-muted" style={{ fontSize: '9px' }}>Girdi (Input)</div>
                      <div className="fw-bold text-dark">{tokenStats.promptTokens.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 rounded bg-white border border-light">
                      <div className="text-muted" style={{ fontSize: '9px' }}>Çıktı (Output)</div>
                      <div className="fw-bold text-dark">{tokenStats.completionTokens.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 rounded bg-white border border-light">
                      <div className="text-muted" style={{ fontSize: '9px' }}>Toplam</div>
                      <div className="fw-bold text-dark">{tokenStats.totalTokens.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center" style={{ fontSize: '11px' }}>
                  <span className="text-muted">Tahmini Harcama (USD):</span>
                  <span className="fw-bold text-success" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    ${tokenStats.estimatedCost.toFixed(6)}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSaveSettings}>
                <div className="settings-form-group">
                  <label className="settings-label">Özel Oturum Bağlamı veya Kataloğu (JSON)</label>
                  <textarea 
                    className="settings-textarea"
                    placeholder='[{"id": 1, "title": "Özel Ürün", "price": 49.99, "category": "Ceket"}]'
                    value={customJson}
                    onChange={(e) => setCustomJson(e.target.value)}
                    rows={8}
                  />
                  <p className="settings-help-text">
                    {"Kendi ürettiğiniz ürün listesini (Format A) veya oturum bağlam JSON nesnesini (Format B) yapıştırarak chatbot'un durumunu anında güncelleyebilirsiniz."}
                  </p>
                  {jsonError && (
                    <p className="settings-error-text">
                      {jsonError}
                    </p>
                  )}
                </div>

                <button type="submit" className="settings-btn-save">
                  Kaydet ve Bağlamı Güncelle
                </button>

                <div className="settings-danger-actions mt-3">
                  {localStorage.getItem('gemini_custom_catalog') && (
                    <button 
                      type="button" 
                      className="btn btn-outline-danger w-100 btn-sm"
                      onClick={handleClearCatalog}
                    >
                      Bağlamı Varsayılana Sıfırla
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Message Feed */}
          <div className="chat-messages">
            {messages.filter(msg => !msg.isHiddenSystem).map((msg, index) => {
              if (msg.isSystem) {
                return (
                  <div key={index} className="chat-message-wrapper system-message">
                    <div className="system-message-bubble">
                      {msg.text}
                    </div>
                  </div>
                );
              }
              const suggestions = getProductSuggestions(msg.text);
              return (
                <div key={index} className={`chat-message-wrapper ${msg.sender}`}>
                  <div className="chat-message-bubble">
                    {msg.text}
                    
                    {/* Render product suggestion cards if matches found */}
                    {suggestions.length > 0 && msg.sender === 'ai' && (
                      <div className="mt-2">
                        {suggestions.map(prod => (
                          <Link 
                            key={prod.id} 
                            to={`/product/${prod.id}`}
                            className="product-suggestion-card"
                            onClick={() => setIsOpen(false)} // Close chat on navigation
                          >
                            <img src={prod.images.main} className="product-suggestion-img" alt={prod.title} />
                            <div className="product-suggestion-info">
                              <p className="product-suggestion-title">{prod.title}</p>
                              <p className="product-suggestion-price">${prod.price}</p>
                            </div>
                            <span className="product-suggestion-buy">
                              İncele <i className="fa fa-arrow-right"></i>
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="chat-message-time">{msg.timestamp}</span>
                </div>
              );
            })}
            
            {isLoading && (
              <div className="chat-message-wrapper ai">
                <div className="chat-message-bubble">
                  <div className="typing-indicator">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions Quick Chips */}
          {messages.length === 1 && !isLoading && (
            <div className="chat-suggestions">
              {suggestionChips.map((s, i) => (
                <button 
                  key={i} 
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Footer Input Area */}
          <div className="chat-input-container">
            <div className="chat-input-wrapper">
              <input 
                type="text" 
                className="chat-input"
                placeholder="Mesajınızı yazın..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                disabled={isLoading}
              />
            </div>
            <button 
              className="chat-send-btn" 
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputMessage.trim()}
              title="Gönder"
            >
              <i className="fa fa-paper-plane"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
